import type {
  ModelDocument,
  ModelType,
  Family,
  Dialect,
  DomainObject,
  Diagnostic,
  Representation,
  Diagram,
} from "../shared/contracts.js";
import { validLayoutCheckpoints } from "../shared/layoutCheckpoints.js";

export class DomainError extends Error {
  constructor(
    public code: string,
    message: string,
    public details: unknown = undefined,
  ) {
    super(message);
    this.name = "DomainError";
  }
}
export const uid = (): string => globalThis.crypto.randomUUID();
export const clone = <T>(value: T): T => structuredClone(value);
export const objectKinds = new Set([
  "concept",
  "entity",
  "table",
  "attribute",
  "column",
  "key",
  "view",
  "index",
  "collection",
  "property",
  "semanticEntity",
  "semanticField",
  "measure",
  "fact",
  "dimension",
  "hierarchy",
  "domain",
  "note",
  "sequence",
  "constraint",
]);
export const rootKinds = new Set([
  "concept",
  "entity",
  "table",
  "view",
  "collection",
  "semanticEntity",
  "fact",
  "dimension",
  "domain",
  "note",
  "sequence",
]);
export const fieldKinds = new Set([
  "attribute",
  "column",
  "property",
  "semanticField",
  "measure",
]);
export const entityKinds = new Set([
  "concept",
  "entity",
  "table",
  "view",
  "collection",
  "semanticEntity",
  "fact",
  "dimension",
]);
export const modelTypes = new Set([
  "conceptual",
  "logical",
  "logical-physical",
  "physical",
]);
export const dialects = new Set([
  "postgresql",
  "oracle",
  "sqlserver",
  "mysql",
  "ase",
]);
export const isRecord = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);
export const idPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export function assertSafeValue(value: unknown, path = "$", depth = 0): void {
  if (depth > 40)
    throw new DomainError("PAYLOAD_TOO_DEEP", "Maximum nesting depth is 40.", {
      path,
    });
  if (typeof value === "number" && !Number.isFinite(value))
    throw new DomainError("INVALID_NUMBER", "Numbers must be finite.", {
      path,
    });
  if (
    typeof value === "function" ||
    typeof value === "symbol" ||
    typeof value === "bigint"
  )
    throw new DomainError(
      "INVALID_VALUE",
      "Only JSON-compatible values are accepted.",
      { path },
    );
  if (typeof value === "string" && value.length > 2000000)
    throw new DomainError("VALUE_TOO_LARGE", "One text value exceeds 2 MB.", {
      path,
    });
  if (value && typeof value === "object")
    for (const [key, child] of Object.entries(value)) {
      if (["__proto__", "prototype", "constructor"].includes(key))
        throw new DomainError("UNSAFE_PROPERTY", "Reserved property name.", {
          path: `${path}.${key}`,
        });
      assertSafeValue(child, `${path}.${key}`, depth + 1);
    }
}
export function createModel(
  name: string,
  modelType: ModelType = "logical",
  family: Family = "relational",
  dialect: Dialect = "postgresql",
): ModelDocument {
  if (!name.trim())
    throw new DomainError("NAME_REQUIRED", "Give the model a name.");
  const modelId = uid(),
    repId = uid();
  const representation: Representation = {
    representationId: repId,
    kind:
      family === "relational"
        ? modelType === "logical-physical"
          ? "logical"
          : modelType
        : ["semantic", "analytical", "document"].includes(family)
          ? (family as "semantic")
          : "logical",
    name:
      modelType === "logical-physical"
        ? "Logical"
        : modelType[0].toUpperCase() + modelType.slice(1),
    origin: "original",
    objectIds: [],
    createdAt: new Date().toISOString(),
  };
  if (representation.kind === "physical")
    representation.targetProfile = dialect;
  const document: ModelDocument = {
    format: "dataglow.model",
    formatVersion: "1.0",
    model: {
      modelId,
      name: name.trim(),
      family,
      modelType,
      activeRepresentation: repId,
      targetProfile: dialect,
      originalConceptualBaseline: null,
    },
    representations: [representation],
    objects: [],
    relationships: [],
    diagrams: [
      {
        diagramId: uid(),
        diagramVersion: 1,
        name: "Main model",
        role: "main",
        perspective:
          family === "relational" ? (representation.kind as "logical") : family,
        representationId: repId,
        notation: "crow-foot",
        appearances: [],
        connectors: [],
      },
    ],
    mappings: [],
    dependencies: [],
    sourceIdentities: [],
    extensions: {},
    manifest: {
      producer: "DataGlow",
      producerVersion: "0.2.0-alpha",
      objectCount: 0,
      relationshipCount: 0,
      requiredExtensions: [],
      optionalExtensions: [],
    },
  };
  if (modelType === "logical-physical" && family === "relational") {
    const physicalId = uid();
    document.representations.push({
      representationId: physicalId,
      kind: "physical",
      name: "Physical",
      origin: "derived",
      sourceRepresentationId: repId,
      targetProfile: dialect,
      objectIds: [],
      createdAt: new Date().toISOString(),
    });
    document.diagrams.push({
      diagramId: uid(),
      diagramVersion: 1,
      name: "Physical diagram",
      role: "main",
      perspective: "physical",
      representationId: physicalId,
      notation: "crow-foot",
      appearances: [],
      connectors: [],
    });
  }
  return document;
}
export function createObject(
  kind: DomainObject["kind"],
  name: string,
  representationId: string,
  ownerObjectId?: string,
): DomainObject {
  const object: DomainObject = {
    objectId: uid(),
    kind,
    representationId,
    name: name.trim(),
    lifecycle: "active",
    tags: [],
    classifications: [],
    sourceIdentities: [],
    extensions: {},
  };
  if (ownerObjectId) object.ownerObjectId = ownerObjectId;
  if (["attribute", "column", "property"].includes(kind))
    object.nullable = true;
  return object;
}
export const getChildren = (doc: ModelDocument, id: string) =>
  doc.objects
    .filter((o) => o.ownerObjectId === id)
    .sort(
      (a, b) =>
        (a.ordinal ?? 0) - (b.ordinal ?? 0) || a.name.localeCompare(b.name),
    );
export const getKeys = (doc: ModelDocument, id: string) =>
  getChildren(doc, id).filter((o) => o.kind === "key");
/** Explicit primary selection takes precedence over legacy role-only metadata. */
export function getSelectedPrimaryKeys(doc: ModelDocument, id: string): DomainObject[] {
  const keys = getKeys(doc, id);
  const selected = keys.filter((key) => key.selectedPrimary === true);
  return selected.length ? selected : keys.filter((key) => key.keyRole === "primary" && key.selectedPrimary !== false);
}
export const getRootObjects = (doc: ModelDocument, representationId?: string) =>
  doc.objects.filter(
    (o) =>
      !o.ownerObjectId &&
      rootKinds.has(o.kind) &&
      (!representationId || o.representationId === representationId),
  );
export function syncDocument(doc: ModelDocument): void {
  for (const rep of doc.representations)
    rep.objectIds = doc.objects
      .filter((o) => o.representationId === rep.representationId)
      .map((o) => o.objectId);
  doc.manifest.objectCount = doc.objects.length;
  doc.manifest.relationshipCount = doc.relationships.length;
}
/** Deterministic presentation IDs make independently upgraded clients agree. */
function presentationId(seed: string): string {
  const chunks = [0, 1, 2, 3].map((salt) => {
    let hash = (2166136261 ^ salt * 16777619) >>> 0;
    for (let i = 0; i < seed.length; i++) hash = Math.imul(hash ^ seed.charCodeAt(i), 16777619) >>> 0;
    return hash.toString(16).padStart(8, "0");
  }).join("");
  return `${chunks.slice(0, 8)}-${chunks.slice(8, 12)}-4${chunks.slice(13, 16)}-a${chunks.slice(17, 20)}-${chunks.slice(20)}`;
}
export const diagramAppearanceId = (diagramId: string, objectId: string): string => presentationId(`main-appearance:${diagramId}:${objectId}`);
/** Add missing appearances beside existing geometry without moving saved objects. */
export function completeMainDiagram(doc: ModelDocument, diagram: Diagram): number {
  if (diagram.role !== "main" || doc.representations.find((r) => r.representationId === diagram.representationId)?.baseline) return 0;
  const represented = new Set(diagram.appearances.map((a) => a.objectId));
  const missing = getRootObjects(doc, diagram.representationId).filter((o) => !represented.has(o.objectId));
  const right = diagram.appearances.length ? Math.max(...diagram.appearances.map((a) => a.position.x + (a.width ?? 320))) + 64 : 40;
  const top = diagram.appearances.length ? Math.min(...diagram.appearances.map((a) => a.position.y)) : 40;
  missing.forEach((object, index) => diagram.appearances.push({
    appearanceId: diagramAppearanceId(diagram.diagramId, object.objectId), objectId: object.objectId,
    position: { x: right + (index % 4) * 400, y: top + Math.floor(index / 4) * 360 },
  }));
  return missing.length;
}
/** Conservative, name-independent migration. Baseline presentation is historical evidence. */
export function migrateDiagramRoles(doc: ModelDocument): { classified: number; created: number; restored: number } {
  const result = { classified: 0, created: 0, restored: 0 };
  for (const rep of doc.representations) {
    if (rep.baseline) continue;
    const diagrams = doc.diagrams.filter((d) => d.representationId === rep.representationId);
    let main = diagrams.find((d) => d.role === "main");
    const legacy = diagrams.filter((d) => d.role === undefined);
    if (!main && diagrams.length === 1 && legacy.length === 1) {
      main = legacy[0]; main.role = "main"; main.roleMigration = "legacy-single"; result.classified++;
    } else {
      for (const d of legacy) { d.role = "view"; d.roleMigration = "legacy-preserved"; result.classified++; }
    }
    if (!main) {
      const diagramId = presentationId(`main-diagram:${doc.model.modelId}:${rep.representationId}`);
      main = {
        diagramId, diagramVersion: 1, name: diagrams.length ? `${rep.name} main model` : "Main model", role: "main", roleMigration: "legacy-recovered",
        perspective: diagrams[0]?.perspective ?? (doc.model.family === "relational" ? rep.kind === "physical" ? "physical" : rep.kind === "conceptual" ? "conceptual" : "logical" : doc.model.family),
        representationId: rep.representationId, notation: diagrams[0]?.notation ?? "crow-foot",
        // Reuse the first historical layout only; combining unrelated layouts can overlap.
        appearances: (diagrams[0]?.appearances ?? []).map((a) => ({ ...clone(a), appearanceId: diagramAppearanceId(diagramId, a.objectId) })),
        connectors: clone(diagrams[0]?.connectors ?? []),
      };
      doc.diagrams.push(main); result.created++;
    }
    result.restored += completeMainDiagram(doc, main);
  }
  return result;
}
export const presentationPalettes = ["teal", "blue", "violet", "amber", "rose", "slate", "green"];
export const entityDetailLevels = ["name", "primary", "business", "business-v2", "mandatory", "all"];
export const isHexColor = (value: unknown): value is string =>
  typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value);
/** Presentation objects have exact, bounded schemas so imported styles cannot carry arbitrary metadata. */
export function validRelationshipStyle(value: unknown): boolean {
  if (!isRecord(value) || Object.keys(value).some((key) => !["color", "width", "lineStyle", "identifyingWidth", "label"].includes(key))) return false;
  if (value.color !== undefined && !isHexColor(value.color)) return false;
  if (value.width !== undefined && (typeof value.width !== "number" || !Number.isFinite(value.width) || value.width < 0.5 || value.width > 12)) return false;
  if (value.identifyingWidth !== undefined && (typeof value.identifyingWidth !== "number" || !Number.isFinite(value.identifyingWidth) || value.identifyingWidth < 0.5 || value.identifyingWidth > 12)) return false;
  if (value.lineStyle !== undefined && !["solid", "dashed", "dotted"].includes(value.lineStyle as string)) return false;
  if (value.label !== undefined) {
    const label = value.label;
    if (!isRecord(label) || Object.keys(label).some((key) => !["orientation", "placement", "offset", "position", "visible", "source"].includes(key))) return false;
    if (label.orientation !== undefined && (typeof label.orientation !== "string" || !["horizontal", "vertical", "aligned"].includes(label.orientation))) return false;
    if (label.placement !== undefined && (typeof label.placement !== "string" || !["above", "below", "on-line"].includes(label.placement))) return false;
    if (label.offset !== undefined && (typeof label.offset !== "number" || !Number.isFinite(label.offset) || label.offset < 0 || label.offset > 200)) return false;
    if (label.position !== undefined && (typeof label.position !== "number" || !Number.isFinite(label.position) || label.position < 0 || label.position > 1)) return false;
    if (label.visible !== undefined && typeof label.visible !== "boolean") return false;
    if (label.source !== undefined && (typeof label.source !== "string" || !["name", "description"].includes(label.source))) return false;
  }
  return true;
}
export function validScopedAppearance(value: unknown, diagram = false): boolean {
  if (!isRecord(value) || Object.keys(value).some((key) => !(diagram ? ["paletteId", "color", "detailLevel", "entityDisplay", "relationship"] : ["paletteId", "color"]).includes(key))) return false;
  if (value.entityDisplay !== undefined && (!isRecord(value.entityDisplay) || Object.keys(value.entityDisplay).some((k) => !["primaryKey", "businessKey", "allAttributes", "dataType", "nullability", "description"].includes(k)) || ["primaryKey", "businessKey", "allAttributes", "dataType", "nullability", "description"].some((k) => typeof (value.entityDisplay as Record<string, unknown>)[k] !== "boolean"))) return false;
  return (value.paletteId === undefined || (typeof value.paletteId === "string" && presentationPalettes.includes(value.paletteId))) &&
    (value.color === undefined || isHexColor(value.color)) &&
    (!diagram || ((value.detailLevel === undefined || (typeof value.detailLevel === "string" && entityDetailLevels.includes(value.detailLevel))) &&
      (value.relationship === undefined || validRelationshipStyle(value.relationship))));
}
function validateModelInternal(input: unknown): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];
  const add = (
    code: string,
    severity: Diagnostic["severity"],
    message: string,
    objectId?: string,
    path?: string,
  ) =>
    diagnostics.push({
      code,
      severity,
      message,
      ...(objectId ? { objectId } : {}),
      ...(path ? { path } : {}),
      helpTopic: "model-validation",
    });
  try {
    assertSafeValue(input);
  } catch (e) {
    add((e as DomainError).code, "error", (e as Error).message);
    return diagnostics;
  }
  if (
    !isRecord(input) ||
    input.format !== "dataglow.model" ||
    input.formatVersion !== "1.0"
  ) {
    add(
      "INVALID_FORMAT",
      "error",
      "Expected dataglow.model formatVersion 1.0.",
    );
    return diagnostics;
  }
  for (const key of [
    "representations",
    "objects",
    "relationships",
    "diagrams",
    "mappings",
    "dependencies",
    "sourceIdentities",
  ])
    if (!Array.isArray(input[key]))
      add(
        "INVALID_SCHEMA",
        "error",
        `${key} must be an array.`,
        undefined,
        key,
      );
  if (
    !isRecord(input.model) ||
    !isRecord(input.manifest) ||
    !isRecord(input.extensions)
  )
    add(
      "INVALID_SCHEMA",
      "error",
      "Model, manifest and extensions must be objects.",
    );
  if (diagnostics.length) return diagnostics;
  const allowedRoot = new Set([
    "format",
    "formatVersion",
    "model",
    "representations",
    "objects",
    "relationships",
    "diagrams",
    "mappings",
    "dependencies",
    "sourceIdentities",
    "extensions",
    "manifest",
  ]);
  for (const key of Object.keys(input))
    if (!allowedRoot.has(key))
      add(
        "UNKNOWN_ROOT_FIELD",
        "error",
        `Unknown canonical envelope field '${key}'. Preserve optional source content inside a declared extension.`,
        undefined,
        key,
      );
  const doc = input as unknown as ModelDocument;
  if (
    doc.objects.length > 50000 ||
    doc.relationships.length > 100000 ||
    doc.diagrams.length > 1000
  ) {
    add(
      "MODEL_LIMIT",
      "error",
      "Model exceeds supported object, relationship or diagram limits.",
    );
    return diagnostics;
  }
  if (doc.model.appearance !== undefined && !validScopedAppearance(doc.model.appearance))
    add("INVALID_MODEL_APPEARANCE", "error", "Model appearance must contain only a valid palette and six-digit color.", doc.model.modelId);
  if (!idPattern.test(doc.model.modelId))
    add("INVALID_ID", "error", "Model identity must be a UUID.");
  if (!doc.model.name?.trim() || doc.model.name.length > 512)
    add(
      "INVALID_NAME",
      "error",
      "Model name must contain 1 to 512 characters.",
    );
  if (!modelTypes.has(doc.model.modelType))
    add(
      "INVALID_MODEL_TYPE",
      "error",
      "Choose conceptual, logical, logical-physical or physical.",
    );
  if (
    ![
      "relational",
      "semantic",
      "analytical",
      "document",
      "graph",
      "process",
    ].includes(doc.model.family)
  )
    add("INVALID_FAMILY", "error", "Unknown modeling family.");
  if (
    !Array.isArray(doc.manifest.requiredExtensions) ||
    !Array.isArray(doc.manifest.optionalExtensions)
  )
    add("INVALID_MANIFEST", "error", "Extension manifests must be arrays.");
  else if (doc.manifest.requiredExtensions.length)
    add(
      "UNSUPPORTED_REQUIRED_EXTENSION",
      "error",
      `Required extensions are not installed: ${doc.manifest.requiredExtensions.join(", ")}.`,
    );
  const allIds = new Set<string>();
  const register = (id: unknown, label: string) => {
    if (typeof id !== "string" || !idPattern.test(id))
      add("INVALID_ID", "error", `${label} identity must be a UUID.`);
    else if (allIds.has(id))
      add("DUPLICATE_ID", "error", `${label} identity is duplicated.`, id);
    else allIds.add(id);
  };
  const reps = new Map<string, Representation>();
  for (const rep of doc.representations) {
    if (!isRecord(rep)) {
      add(
        "INVALID_REPRESENTATION",
        "error",
        "Representation must be an object.",
      );
      continue;
    }
    register(rep.representationId, "Representation");
    reps.set(rep.representationId, rep);
    if (
      !Array.isArray(rep.objectIds) ||
      ![
        "conceptual",
        "logical",
        "physical",
        "logical-physical",
        "semantic",
        "analytical",
        "document",
      ].includes(rep.kind)
    )
      add(
        "INVALID_REPRESENTATION",
        "error",
        "Representation kind or members are invalid.",
        rep.representationId,
      );
  }
  if (
    doc.model.modelType === "logical-physical" &&
    (![...reps.values()].some((r) => r.kind === "logical") ||
      ![...reps.values()].some((r) => r.kind === "physical"))
  )
    add(
      "LINKED_REPRESENTATIONS_REQUIRED",
      "error",
      "A logical-physical model requires independent logical and physical representations.",
    );
  if (!reps.has(doc.model.activeRepresentation))
    add(
      "MISSING_REPRESENTATION",
      "error",
      "Active representation does not exist.",
    );
  if (doc.model.originalConceptualBaseline) {
    const baseline = reps.get(doc.model.originalConceptualBaseline);
    if (!baseline || baseline.kind !== "conceptual" || !baseline.baseline)
      add(
        "INVALID_BASELINE",
        "error",
        "Original conceptual baseline must reference a preserved conceptual representation.",
      );
  }
  const objects = new Map<string, DomainObject>();
  for (const o of doc.objects) {
    if (!isRecord(o)) {
      add("INVALID_OBJECT", "error", "Every object must be an object.");
      continue;
    }
    register(o.objectId, "Object");
    objects.set(o.objectId, o);
  }
  const namespaces = new Map<string, string>();
  for (const o of doc.objects) {
    if (!isRecord(o)) continue;
    if (!objectKinds.has(o.kind))
      add(
        "INVALID_OBJECT_KIND",
        "error",
        `Unsupported object kind ${o.kind}.`,
        o.objectId,
      );
    if (typeof o.name !== "string" || !o.name.trim() || o.name.length > 512) {
      add(
        "INVALID_NAME",
        "error",
        "Object name must contain 1 to 512 characters.",
        o.objectId,
      );
      continue;
    }
    for (const flag of [
      "nullable",
      "generated",
      "unique",
      "selectedPrimary",
    ] as const)
      if (o[flag] !== undefined && typeof o[flag] !== "boolean")
        add(
          "INVALID_BOOLEAN",
          "error",
          `${flag} must be true or false.`,
          o.objectId,
        );
    if (
      o.ordinal !== undefined &&
      (!Number.isSafeInteger(o.ordinal) || o.ordinal < 0)
    )
      add(
        "INVALID_ORDINAL",
        "error",
        "Field ordinal must be a non-negative safe integer.",
        o.objectId,
      );
    if (
      o.keyRole !== undefined &&
      !["primary", "candidate", "business", "surrogate", "unique"].includes(
        o.keyRole,
      )
    )
      add("INVALID_KEY_ROLE", "error", "Unknown identifier role.", o.objectId);
    if (o.members !== undefined && !Array.isArray(o.members))
      add(
        "INVALID_MEMBERS",
        "error",
        "Key members must be an array.",
        o.objectId,
      );
    if (!reps.has(o.representationId))
      add(
        "MISSING_REPRESENTATION",
        "error",
        "Object representation does not exist.",
        o.objectId,
      );
    if (
      !Array.isArray(o.tags) ||
      !Array.isArray(o.classifications) ||
      !Array.isArray(o.sourceIdentities) ||
      !isRecord(o.extensions)
    )
      add(
        "INVALID_OBJECT_ENVELOPE",
        "error",
        "Tags, classifications, provenance and extensions are required.",
        o.objectId,
      );
    if (!["active", "deprecated", "archived"].includes(o.lifecycle))
      add(
        "INVALID_LIFECYCLE",
        "error",
        "Unknown object lifecycle.",
        o.objectId,
      );
    const nsKind = entityKinds.has(o.kind)
      ? "entity"
      : fieldKinds.has(o.kind)
        ? "field"
        : o.kind;
    const ns = `${o.representationId}:${o.ownerObjectId ?? o.schema ?? ""}:${nsKind}:${(o.extensions?.engineering as any)?.quotedIdentifier ? o.name : doc.model.targetProfile === "oracle" && reps.get(o.representationId)?.kind === "physical" ? o.name.normalize("NFKC").toLocaleUpperCase("en-US") : o.name.normalize("NFKC").toLocaleLowerCase("en-US")}`;
    if (namespaces.has(ns))
      add(
        "NAME_COLLISION",
        "error",
        `Name '${o.name}' is already used in this namespace.`,
        o.objectId,
      );
    else namespaces.set(ns, o.objectId);
    if (o.ownerObjectId) {
      const owner = objects.get(o.ownerObjectId);
      if (!owner)
        add(
          "MISSING_OWNER",
          "error",
          "Owning object does not exist.",
          o.objectId,
        );
      else if (
        !entityKinds.has(owner.kind) &&
        owner.kind !== "property" &&
        owner.kind !== "hierarchy"
      )
        add(
          "INVALID_OWNER_KIND",
          "error",
          "Owned fields and keys require an entity, collection or structured property owner.",
          o.objectId,
        );
      else if (owner.representationId !== o.representationId)
        add(
          "CROSS_REPRESENTATION_OWNER",
          "error",
          "Object and owner must belong to the same representation.",
          o.objectId,
        );
      let cursor: DomainObject | undefined = o;
      const seen = new Set<string>();
      while (cursor?.ownerObjectId) {
        if (seen.has(cursor.objectId)) {
          add(
            "OWNER_CYCLE",
            "error",
            "Object ownership must be acyclic.",
            o.objectId,
          );
          break;
        }
        seen.add(cursor.objectId);
        cursor = objects.get(cursor.ownerObjectId);
      }
    } else if (
      fieldKinds.has(o.kind) ||
      o.kind === "key" ||
      o.kind === "index" ||
      o.kind === "constraint"
    )
      add(
        "OWNER_REQUIRED",
        "error",
        "Attributes, columns and keys require an owner.",
        o.objectId,
      );
    if (
      o.type &&
      (!isRecord(o.type) ||
        typeof o.type.name !== "string" ||
        !o.type.name.trim())
    )
      add("INVALID_TYPE", "error", "A type requires a name.", o.objectId);
    const typeDialect = (o.extensions.engineering as { typeDialect?: string } | undefined)?.typeDialect ?? reps.get(o.representationId)?.targetProfile ?? doc.model.targetProfile;
    const signedNumeric = /^(number|numeric|decimal)$/i.test(o.type?.name ?? "") && ["oracle", "postgresql"].includes(typeDialect ?? "");
    const minScale = signedNumeric ? typeDialect === "oracle" ? -84 : -1000 : 0;
    const maxScale = signedNumeric ? typeDialect === "oracle" ? 127 : 1000 : undefined;
    if (o.type) for (const field of ["length", "precision", "scale"] as const) {
      const value = o.type[field], minimum = field === "scale" ? minScale : 1;
      const maximum = field === "scale" ? maxScale : field === "precision" && signedNumeric ? typeDialect === "oracle" ? 38 : 1000 : undefined;
      if (value !== undefined && (!Number.isInteger(value) || value < minimum || (maximum !== undefined && value > maximum)))
        add("INVALID_TYPE_ARGUMENT", "error", `${field} must be an integer from ${minimum}${maximum !== undefined ? " to " + maximum : " upwards"} for ${typeDialect ?? "this datatype"}.`, o.objectId);
    }
    if (o.type?.precision && o.type.scale !== undefined && o.type.scale > o.type.precision && !signedNumeric)
      add("INVALID_TYPE_ARGUMENT", "error", "Scale cannot exceed precision for this target datatype.", o.objectId);
    if (o.kind === "column" && !o.type && !(o.extensions.engineering as { inferredType?: boolean } | undefined)?.inferredType)
      add(
        "TYPE_REQUIRED",
        "warning",
        "Choose a physical datatype before generating DDL.",
        o.objectId,
      );
    if (o.kind === "key" || o.kind === "index") {
      if ((!Array.isArray(o.members) || !o.members.length) && !o.expression)
        add(
          "KEY_MEMBERS_REQUIRED",
          "warning",
          "Choose the ordered members of this key.",
          o.objectId,
        );
      else if (o.members) {
        if (new Set(o.members).size !== o.members.length)
          add(
            "DUPLICATE_KEY_MEMBER",
            "error",
            "A key cannot include the same member twice.",
            o.objectId,
          );
        for (const id of o.members) {
          const member = objects.get(id);
          if (
            !member ||
            member.ownerObjectId !== o.ownerObjectId ||
            !fieldKinds.has(member.kind)
          )
            add(
              "INVALID_KEY_MEMBER",
              "error",
              "Every key member must be a field of its owning entity.",
              o.objectId,
            );
          else if (
            getSelectedPrimaryKeys(doc, o.ownerObjectId!).some((key) => key.objectId === o.objectId) &&
            member.nullable !== false
          )
            add(
              "PRIMARY_KEY_NULLABLE",
              "warning",
              "Primary identifier members must be required.",
              member.objectId,
            );
        }
      }
    }
  }
  for (const rep of reps.values())
    if (Array.isArray(rep.objectIds)) {
      const actual = doc.objects
        .filter((o) => o?.representationId === rep.representationId)
        .map((o) => o.objectId);
      if (
        actual.length !== rep.objectIds.length ||
        actual.some((id) => !rep.objectIds.includes(id)) ||
        new Set(rep.objectIds).size !== rep.objectIds.length
      )
        add(
          "REPRESENTATION_MEMBERSHIP",
          "error",
          "Representation members differ from its objects.",
          rep.representationId,
        );
      if (rep.sourceRepresentationId && !reps.has(rep.sourceRepresentationId))
        add(
          "MISSING_SOURCE_REPRESENTATION",
          "error",
          "Derived representation source is missing.",
          rep.representationId,
        );
    }
  for (const o of doc.objects.filter((o) => o && entityKinds.has(o.kind))) {
    const keys = getSelectedPrimaryKeys(doc, o.objectId);
    if (keys.length > 1)
      add(
        "MULTIPLE_PRIMARY_KEYS",
        "error",
        "An entity may have multiple candidates but only one selected primary identifier.",
        o.objectId,
      );
    if (!keys.length && ["entity", "table"].includes(o.kind))
      add(
        "IDENTITY_UNRESOLVED",
        "warning",
        "No primary identifier is selected. Define a business identifier or explicitly choose a physical key strategy.",
        o.objectId,
      );
  }
  const relationshipIds = new Set<string>();
  for (const r of doc.relationships) {
    if (!isRecord(r)) {
      add("INVALID_RELATIONSHIP", "error", "Relationship must be an object.");
      continue;
    }
    register(r.relationshipId, "Relationship");
    relationshipIds.add(r.relationshipId);
    const s = objects.get(r.sourceObjectId),
      t = objects.get(r.targetObjectId);
    if ((s && !entityKinds.has(s.kind)) || (t && !entityKinds.has(t.kind)))
      add(
        "INVALID_RELATIONSHIP_END_KIND",
        "error",
        "Relationships must connect entities, not individual columns or keys.",
        r.relationshipId,
      );
    if (
      typeof r.sourceOptional !== "boolean" ||
      typeof r.targetOptional !== "boolean" ||
      typeof r.identifying !== "boolean"
    )
      add(
        "INVALID_RELATIONSHIP_FLAGS",
        "error",
        "Optionality and identifying flags must be booleans.",
        r.relationshipId,
      );
    if (!s || !t)
      add(
        "MISSING_RELATIONSHIP_END",
        "error",
        "Both relationship entities must exist.",
        r.relationshipId,
      );
    else if (
      s.representationId !== r.representationId ||
      t.representationId !== r.representationId
    )
      add(
        "CROSS_REPRESENTATION_RELATIONSHIP",
        "error",
        "Use a mapping for cross-representation references.",
        r.relationshipId,
      );
    if (
      !["one", "many"].includes(r.sourceCardinality) ||
      !["one", "many"].includes(r.targetCardinality)
    )
      add(
        "INVALID_CARDINALITY",
        "error",
        "Cardinality must be one or many.",
        r.relationshipId,
      );
    for (const [ids, owner] of [
      [r.sourceAttributeIds, s],
      [r.targetAttributeIds, t],
    ] as const)
      if (ids) {
        if (!Array.isArray(ids))
          add(
            "INVALID_RELATIONSHIP_FIELDS",
            "error",
            "Relationship fields must be arrays.",
            r.relationshipId,
          );
        else
          for (const id of ids)
            if (
              !objects.has(id) ||
              objects.get(id)!.ownerObjectId !== owner?.objectId
            )
              add(
                "INVALID_RELATIONSHIP_FIELD",
                "error",
                "Relationship field does not belong to its endpoint.",
                r.relationshipId,
              );
      }
    if (
      r.sourceAttributeIds?.length !== r.targetAttributeIds?.length &&
      (r.sourceAttributeIds?.length || r.targetAttributeIds?.length)
    )
      add(
        "RELATIONSHIP_ARITY",
        "error",
        "Both ends require the same number of ordered fields.",
        r.relationshipId,
      );
    // Classification is semantic: line styling never silently rewrites keys.
    // Parent/child direction follows cardinality; physical FK forms conventionally
    // use many/source as the child and one/target as the referenced parent.
    if (s && t && ["logical", "physical", "logical-physical"].includes(reps.get(r.representationId)?.kind ?? "")) {
      if (r.identifying && r.sourceCardinality === "many" && r.targetCardinality === "many") {
        add("IDENTIFYING_MANY_TO_MANY", "warning", "A many-to-many relationship has no single child key. Resolve it through an associative entity before assigning identifying key propagation.", r.relationshipId);
      } else {
        const parentIsSource = r.sourceCardinality === "one" && r.targetCardinality === "many";
        const parent = parentIsSource ? s : t, child = parentIsSource ? t : s;
        const parentFields = parentIsSource ? r.sourceAttributeIds : r.targetAttributeIds;
        const childFields = parentIsSource ? r.targetAttributeIds : r.sourceAttributeIds;
        const primary = getSelectedPrimaryKeys(doc, child.objectId)[0]?.members ?? [];
        if (r.identifying) {
          if (!Array.isArray(parentFields) || !parentFields.length || !Array.isArray(childFields) || !childFields.length)
            add("IDENTIFYING_KEY_MAPPING_REQUIRED", "warning", `Map the identifying fields from parent '${parent.name}' to child '${child.name}'. Choosing identifying does not automatically invent fields or change the child's primary key.`, r.relationshipId);
          else {
            const parentKeys = getKeys(doc, parent.objectId);
            const matchesParentKey = parentKeys.some((key) => key.members?.length === parentFields.length && key.members.every((id, index) => parentFields[index] === id));
            if (!matchesParentKey) add("IDENTIFYING_PARENT_KEY_MISMATCH", "warning", `The mapped fields of parent '${parent.name}' must match an ordered declared key. Review the parent field order.`, r.relationshipId);
            if (!primary.length || childFields.some((id) => !primary.includes(id)))
              add("IDENTIFYING_CHILD_KEY_MISMATCH", "warning", `The mapped fields in child '${child.name}' must be part of its selected primary key for this relationship to be identifying.`, r.relationshipId);
          }
        } else if (Array.isArray(childFields) && childFields.length && primary.length && childFields.every((id) => primary.includes(id))) {
          add("NON_IDENTIFYING_PRIMARY_KEY_MAPPING", "warning", `All mapped fields in child '${child.name}' are part of its primary key. Review whether this relationship should be identifying.`, r.relationshipId);
        }
      }
    }
  }
  for (const diagram of doc.diagrams) {
    if (
      !isRecord(diagram) ||
      !Array.isArray(diagram.appearances) ||
      !Array.isArray(diagram.connectors)
    ) {
      add(
        "INVALID_DIAGRAM",
        "error",
        "Diagram appearances and connectors must be arrays.",
      );
      continue;
    }
    register(diagram.diagramId, "Diagram");
    if (typeof diagram.name !== "string" || !diagram.name.trim() || diagram.name.length > 512)
      add("INVALID_DIAGRAM_NAME", "error", "Diagram names must contain 1 to 512 characters.", diagram.diagramId);
    if (diagram.role !== undefined && !["main", "subject-area", "view"].includes(diagram.role))
      add("INVALID_DIAGRAM_ROLE", "error", "Diagram role must be main, subject-area, or view.", diagram.diagramId);
    if (diagram.role === "main" && doc.diagrams.some((other) => other !== diagram && other.role === "main" && other.representationId === diagram.representationId))
      add("DUPLICATE_MAIN_DIAGRAM", "error", "Each representation has one main diagram.", diagram.diagramId);
    if (diagram.parentDiagramId !== undefined) {
      const parent = doc.diagrams.find((d) => d.diagramId === diagram.parentDiagramId);
      if (diagram.role !== "view" || !parent || parent.role !== "subject-area" || parent.representationId !== diagram.representationId)
        add("INVALID_VIEW_PARENT", "error", "A nested view belongs to a subject area in the same representation.", diagram.diagramId);
      else if (diagram.appearances.some((a) => !parent.appearances.some((p) => p.objectId === a.objectId)))
        add("VIEW_OUTSIDE_SUBJECT_AREA", "error", "Add these entities explicitly to the parent subject area before including them in its view.", diagram.diagramId);
    }
    if (!validLayoutCheckpoints(diagram.layoutCheckpoints))
      add("INVALID_LAYOUT_CHECKPOINTS", "error", "Saved layouts require unique names and identities, finite geometry, and at most 30 checkpoints with 10,000 entities and 20,000 connectors each.", diagram.diagramId);
    if (diagram.appearance !== undefined && !validScopedAppearance(diagram.appearance, true))
      add("INVALID_DIAGRAM_APPEARANCE", "error", "Diagram appearance contains an invalid palette, detail level or relationship style.", diagram.diagramId);
    if (!reps.has(diagram.representationId))
      add(
        "MISSING_REPRESENTATION",
        "error",
        "Diagram representation does not exist.",
        diagram.diagramId,
      );
    if (
      !Number.isSafeInteger(diagram.diagramVersion) ||
      diagram.diagramVersion < 1
    )
      add(
        "INVALID_DIAGRAM_VERSION",
        "error",
        "Diagram version must be a positive safe integer.",
        diagram.diagramId,
      );
    const displayed = new Set<string>();
    for (const a of diagram.appearances) {
      if (!isRecord(a)) {
        add("INVALID_APPEARANCE", "error", "Appearance must be an object.");
        continue;
      }
      register(a.appearanceId, "Appearance");
      if ((a.color !== undefined && !isHexColor(a.color)) ||
          (a.pinned !== undefined && typeof a.pinned !== "boolean") ||
          (a.sizeLocked !== undefined && typeof a.sizeLocked !== "boolean") ||
          (a.collapsed !== undefined && typeof a.collapsed !== "boolean"))
        add("INVALID_APPEARANCE_STYLE", "error", "Entity appearance requires a six-digit color and boolean pinned, sizeLocked and collapsed flags.", a.appearanceId);
      const object = objects.get(a.objectId);
      if (!object || object.representationId !== diagram.representationId)
        add(
          "INVALID_APPEARANCE_REFERENCE",
          "error",
          "Appearance must reference an object in the diagram representation.",
          a.appearanceId,
        );
      if (
        !isRecord(a.position) ||
        !Number.isFinite(a.position.x) ||
        !Number.isFinite(a.position.y)
      )
        add(
          "INVALID_GEOMETRY",
          "error",
          "Appearance coordinates must be finite.",
          a.appearanceId,
        );
      if (
        (a.width !== undefined &&
          (!Number.isFinite(a.width) || a.width <= 0)) ||
        (a.height !== undefined &&
          (!Number.isFinite(a.height) || a.height <= 0))
      )
        add(
          "INVALID_GEOMETRY",
          "error",
          "Appearance dimensions must be positive finite numbers.",
          a.appearanceId,
        );
      if (displayed.has(a.objectId))
        add(
          "DUPLICATE_APPEARANCE",
          "error",
          "An object can appear once per diagram.",
          a.appearanceId,
        );
      displayed.add(a.objectId);
    }
    const connectors = new Set<string>();
    for (const c of diagram.connectors) {
      if (!isRecord(c)) {
        add("INVALID_CONNECTOR", "error", "Connector must be an object.");
        continue;
      }
      if (c.style !== undefined && !validRelationshipStyle(c.style))
        add("INVALID_RELATIONSHIP_STYLE", "error", "Relationship style has invalid color, width or label settings.", c.relationshipId);
      if (connectors.has(c.relationshipId))
        add(
          "DUPLICATE_CONNECTOR",
          "error",
          "Relationship can have one connector per diagram.",
          c.relationshipId,
        );
      connectors.add(c.relationshipId);
      for (const side of [c.sourceSide, c.targetSide])
        if (
          side !== undefined &&
          !["top", "right", "bottom", "left"].includes(side)
        )
          add(
            "INVALID_CONNECTOR_SIDE",
            "error",
            "Attachment side must be top, right, bottom or left.",
            c.relationshipId,
          );
      for (const fraction of [c.sourceFraction, c.targetFraction])
        if (fraction !== undefined && (typeof fraction !== "number" || !Number.isFinite(fraction) || fraction < 0 || fraction > 1))
          add("INVALID_CONNECTOR_FRACTION", "error", "Attachment fraction must be a finite number between 0 and 1.", c.relationshipId);
      if (
        c.waypoints !== undefined &&
        (!Array.isArray(c.waypoints) ||
          c.waypoints.some(
            (p) => !p || !Number.isFinite(p.x) || !Number.isFinite(p.y),
          ))
      )
        add(
          "INVALID_WAYPOINT",
          "error",
          "Manual waypoints require finite coordinates.",
          c.relationshipId,
        );
      const rel = doc.relationships.find(
        (r) => r.relationshipId === c.relationshipId,
      );
      if (!rel || rel.representationId !== diagram.representationId)
        add(
          "INVALID_CONNECTOR_REFERENCE",
          "error",
          "Connector relationship does not exist in this representation.",
          c.relationshipId,
        );
    }
  }
  for (const mapping of doc.mappings) {
    if (
      !isRecord(mapping) ||
      !Array.isArray(mapping.sourceRefs) ||
      !Array.isArray(mapping.destinationRefs)
    ) {
      add(
        "INVALID_MAPPING",
        "error",
        "Mappings require source and destination arrays.",
      );
      continue;
    }
    register(mapping.mappingId, "Mapping");
    for (const ref of [...mapping.sourceRefs, ...mapping.destinationRefs])
      if (
        ref.modelId === doc.model.modelId &&
        (!objects.has(ref.objectId) ||
          objects.get(ref.objectId)!.representationId !== ref.representationId)
      )
        add(
          "INVALID_MAPPING_REFERENCE",
          "error",
          "Local mapping reference does not exist.",
          mapping.mappingId,
        );
  }
  return diagnostics;
}

/** Never throws on untrusted imported JSON. */
export function validateModel(input: unknown): Diagnostic[] {
  try {
    return validateModelInternal(input);
  } catch (error) {
    return [
      {
        code: "INVALID_SCHEMA",
        severity: "error",
        message: "The model contains malformed fields or object records.",
        helpTopic: "model-validation",
      },
    ];
  }
}

/** Normalize the documented empty envelope and unambiguous representation aliases.
 * No semantic object is synthesized and unknown envelope fields are never dropped. */
export function normalizeModel(input: unknown): ModelDocument {
  assertSafeValue(input);
  if (!isRecord(input) || !isRecord(input.model))
    throw new DomainError(
      "INVALID_FORMAT",
      "A canonical model descriptor is required.",
    );
  const doc = clone(input) as unknown as ModelDocument;
  const requiredArrays = [
    "representations",
    "objects",
    "relationships",
    "diagrams",
    "mappings",
    "dependencies",
    "sourceIdentities",
  ];
  if (requiredArrays.some((key) => !Array.isArray((doc as any)[key])))
    throw new DomainError(
      "INVALID_SCHEMA",
      "All canonical collections must be arrays.",
    );
  const alias = doc.model.activeRepresentation;
  const supportedAliases = [
    "conceptual",
    "logical",
    "physical",
    "semantic",
    "analytical",
    "document",
  ];
  if (
    doc.representations.length === 0 &&
    doc.objects.length === 0 &&
    doc.relationships.length === 0 &&
    doc.diagrams.length === 0 &&
    supportedAliases.includes(alias)
  ) {
    const repId = uid(),
      kind = alias as Representation["kind"];
    doc.representations.push({
      representationId: repId,
      kind,
      name: alias[0].toUpperCase() + alias.slice(1),
      origin: "original",
      objectIds: [],
      ...(kind === "physical" && doc.model.targetProfile
        ? { targetProfile: doc.model.targetProfile }
        : {}),
    });
    doc.model.activeRepresentation = repId;
    if (doc.model.modelType === "logical-physical") {
      const missing = kind === "logical" ? "physical" : "logical",
        id = uid();
      doc.representations.push({
        representationId: id,
        kind: missing,
        name: missing[0].toUpperCase() + missing.slice(1),
        origin: "original",
        objectIds: [],
      });
    }
  } else if (supportedAliases.includes(alias)) {
    const matches = doc.representations.filter(
      (r) => r.kind === alias && !r.baseline,
    );
    if (matches.length !== 1)
      throw new DomainError(
        "AMBIGUOUS_REPRESENTATION",
        "The symbolic active representation must resolve to exactly one representation.",
        { alias, matches: matches.map((r) => r.representationId) },
      );
    doc.model.activeRepresentation = matches[0].representationId;
  }
  if (doc.diagrams.length === 0 && doc.representations.length) {
    for (const rep of doc.representations) {
      rep.name ??= rep.kind[0].toUpperCase() + rep.kind.slice(1);
      doc.diagrams.push({
        diagramId: uid(),
        diagramVersion: 1,
        name: `${rep.name} diagram`,
        perspective:
          doc.model.family === "relational"
            ? ((rep.kind === "logical-physical"
                ? "logical"
                : rep.kind) as "conceptual")
            : "document" === doc.model.family
              ? "document"
              : doc.model.family,
        representationId: rep.representationId,
        notation: "crow-foot",
        appearances: doc.objects
          .filter(
            (o) =>
              o.representationId === rep.representationId && !o.ownerObjectId,
          )
          .map((o, i) => ({
            appearanceId: uid(),
            objectId: o.objectId,
            position: { x: (i % 4) * 340, y: Math.floor(i / 4) * 340 },
          })),
        connectors: [],
      });
    }
  }
  if (isRecord(doc.manifest)) {
    doc.manifest.objectCount = doc.objects.length;
    doc.manifest.relationshipCount = doc.relationships.length;
  }
  const errors = validateModel(doc).filter((d) => d.severity === "error");
  if (errors.length)
    throw new DomainError(
      "MODEL_INVALID",
      "The canonical model is invalid.",
      errors,
    );
  migrateDiagramRoles(doc);
  return doc;
}
